/**
 * SignatureList Component
 *
 * Lists all signatures captured for a project.
 * Shows signature thumbnails and details.
 *
 * @module pages/technician/signature/SignatureList
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  FaArrowLeft,
  FaFileSignature,
  FaCheckCircle,
  FaBox,
  FaShieldAlt,
  FaClipboardList,
  FaPlus,
  FaSpinner,
  FaExclamationCircle,
  FaTimes,
  FaUser,
  FaClock,
  FaMapMarkerAlt,
} from 'react-icons/fa';
import { useAuth } from '../../../contexts/AuthContext';
import { getSignatures } from '../../../services/signatureService';
import {
  SIGNATURE_TYPE,
  SIGNATURE_TYPE_LABELS,
  canCaptureSignatureType,
} from '../../../utils/constants';
import './SignatureCapture.css';

// Signature type icons — keys must be real SIGNATURE_TYPE members, which in
// turn mirror the live CHECK constraint (see constants.js). Previously these
// referenced CLIENT_APPROVAL / WORK_COMPLETION / SAFETY_CHECKLIST /
// DAILY_REPORT, none of which existed, so every key resolved to `undefined`
// and collapsed into a single bogus entry (#311).
const TYPE_ICONS = {
  [SIGNATURE_TYPE.CLIENT_ACCEPTANCE]: FaCheckCircle,
  [SIGNATURE_TYPE.PROJECT_CLOSURE]: FaClipboardList,
  [SIGNATURE_TYPE.INCIDENT_ACKNOWLEDGMENT]: FaShieldAlt,
  [SIGNATURE_TYPE.INVOICE_RECEIPT]: FaBox,
};

const SignatureList = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { profile } = useAuth();

  // Tipos que este rol puede capturar (#525). Hasta ahora la pantalla ofrecía
  // los cuatro botones a todo el mundo, incluidos `project_closure` e
  // `invoice_receipt`, que son actos de oficina: el técnico llegaba al canvas,
  // firmaba, y recién ahí la RLS lo rebotaba. Se recorta acá para que el
  // callejón no exista.
  const capturableTypes = Object.values(SIGNATURE_TYPE).filter((type) =>
    canCaptureSignatureType(profile?.role, type)
  );
  const defaultType = capturableTypes[0] || SIGNATURE_TYPE.CLIENT_ACCEPTANCE;

  const [signatures, setSignatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedSignature, setSelectedSignature] = useState(null);

  // Load signatures
  const loadSignatures = useCallback(async () => {
    if (!projectId) return;

    try {
      setLoading(true);
      setError(null);

      const { data, error: fetchError } = await getSignatures(projectId);

      if (fetchError) throw fetchError;

      setSignatures(data || []);
    } catch (err) {
      console.error('[SignatureList] Load error:', err);
      setError(err.message || 'Error al cargar firmas');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    loadSignatures();
  }, [loadSignatures]);

  // Get type icon
  const getTypeIcon = (type) => {
    const Icon = TYPE_ICONS[type] || FaFileSignature;
    return <Icon />;
  };

  // Get type label
  const getTypeLabel = (type) => {
    return SIGNATURE_TYPE_LABELS[type] || type;
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-CR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // Format time
  const formatTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleTimeString('es-CR', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Format location
  const formatLocation = (sig) => {
    if (!sig.latitude || !sig.longitude) return null;
    return `${sig.latitude.toFixed(4)}, ${sig.longitude.toFixed(4)}`;
  };

  // Navigate to capture new signature
  const handleNewSignature = (type = defaultType) => {
    navigate(`/tecnico/proyectos/${projectId}/firma?type=${type}`);
  };

  // Group signatures by date
  // NOTE: live column is created_at (there is no captured_at) — see #305/#311.
  const groupedSignatures = signatures.reduce((groups, sig) => {
    const date = formatDate(sig.created_at);
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(sig);
    return groups;
  }, {});

  // Loading state
  if (loading) {
    return (
      <div className="signature-list-loading">
        <FaSpinner className="spinner" />
        <p>Cargando firmas...</p>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="signature-list-error">
        <FaExclamationCircle className="error-icon" />
        <p>{error}</p>
        <button className="retry-btn" onClick={loadSignatures}>
          Reintentar
        </button>
        <button className="back-btn" onClick={() => navigate(-1)}>
          Volver
        </button>
      </div>
    );
  }

  return (
    <div className="signature-list">
      {/* Header */}
      <div className="signature-list-header">
        <button
          className="back-button"
          onClick={() => navigate(-1)}
          aria-label="Volver"
        >
          <FaArrowLeft />
        </button>
        <div className="header-info">
          <h1 className="page-title">Firmas</h1>
          <p className="page-subtitle">
            {signatures.length} firma{signatures.length !== 1 ? 's' : ''} capturada{signatures.length !== 1 ? 's' : ''}
          </p>
        </div>
        <button
          className="add-signature-btn"
          onClick={() => handleNewSignature()}
          aria-label="Nueva firma"
        >
          <FaPlus />
        </button>
      </div>

      {/* Quick actions */}
      <div className="quick-actions">
        <h2 className="section-title">Capturar nueva firma</h2>
        <div className="action-buttons">
          {capturableTypes.map((value) => {
            const Icon = TYPE_ICONS[value] || FaFileSignature;
            return (
              <button
                key={value}
                className="action-button"
                onClick={() => handleNewSignature(value)}
              >
                <div className="action-icon">
                  <Icon />
                </div>
                <span className="action-label">{getTypeLabel(value)}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Signatures list */}
      {signatures.length === 0 ? (
        <div className="empty-state">
          <FaFileSignature className="empty-icon" />
          <h3>No hay firmas</h3>
          <p>Aun no se han capturado firmas para este proyecto.</p>
          <button
            className="primary-btn"
            onClick={() => handleNewSignature()}
          >
            <FaPlus /> Capturar Primera Firma
          </button>
        </div>
      ) : (
        <div className="signatures-container">
          {Object.entries(groupedSignatures).map(([date, sigs]) => (
            <div key={date} className="date-group">
              <h3 className="date-header">{date}</h3>
              <div className="signatures-grid">
                {sigs.map((sig) => (
                  <div
                    key={sig.id}
                    className="signature-card"
                    onClick={() => setSelectedSignature(sig)}
                  >
                    {/* Signature thumbnail — the base64 image lives inline in
                        signature_data (TEXT); there is no signature_url column. */}
                    <div className="signature-thumbnail">
                      {sig.signature_data ? (
                        <img
                          src={sig.signature_data}
                          alt={`Firma de ${sig.signer_name}`}
                          loading="lazy"
                        />
                      ) : (
                        <div className="no-image">
                          <FaFileSignature />
                        </div>
                      )}
                    </div>

                    {/* Signature info */}
                    <div className="signature-info">
                      <div className="signature-type">
                        {getTypeIcon(sig.signature_type)}
                        <span>{getTypeLabel(sig.signature_type)}</span>
                      </div>
                      <div className="signer-name">{sig.signer_name}</div>
                      {sig.signer_role && (
                        <div className="signer-role">{sig.signer_role}</div>
                      )}
                      <div className="signature-meta">
                        <span className="capture-time">
                          <FaClock /> {formatTime(sig.created_at)}
                        </span>
                        {sig.captured_by_profile && (
                          <span className="captured-by">
                            <FaUser /> {sig.captured_by_profile.full_name}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Signature detail modal */}
      {selectedSignature && (
        <div
          className="modal-overlay"
          onClick={() => setSelectedSignature(null)}
        >
          <div
            className="signature-detail-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h2>Detalle de Firma</h2>
              <button
                className="modal-close"
                onClick={() => setSelectedSignature(null)}
              >
                <FaTimes />
              </button>
            </div>

            <div className="modal-body">
              {/* Full signature image */}
              <div className="full-signature">
                {selectedSignature.signature_data ? (
                  <img
                    src={selectedSignature.signature_data}
                    alt={`Firma de ${selectedSignature.signer_name}`}
                  />
                ) : (
                  <div className="no-image-large">
                    <FaFileSignature />
                    <span>Sin imagen</span>
                  </div>
                )}
              </div>

              {/* Signature details */}
              <div className="signature-details">
                <div className="detail-row">
                  <span className="detail-label">Tipo:</span>
                  <span className="detail-value">
                    {getTypeIcon(selectedSignature.signature_type)}
                    {getTypeLabel(selectedSignature.signature_type)}
                  </span>
                </div>

                <div className="detail-row">
                  <span className="detail-label">Firmante:</span>
                  <span className="detail-value">
                    {selectedSignature.signer_name}
                  </span>
                </div>

                {selectedSignature.signer_role && (
                  <div className="detail-row">
                    <span className="detail-label">Cargo:</span>
                    <span className="detail-value">
                      {selectedSignature.signer_role}
                    </span>
                  </div>
                )}

                {selectedSignature.signer_company && (
                  <div className="detail-row">
                    <span className="detail-label">Empresa:</span>
                    <span className="detail-value">
                      {selectedSignature.signer_company}
                    </span>
                  </div>
                )}

                <div className="detail-row">
                  <span className="detail-label">Fecha:</span>
                  <span className="detail-value">
                    {new Date(selectedSignature.created_at).toLocaleString('es-CR')}
                  </span>
                </div>

                {formatLocation(selectedSignature) && (
                  <div className="detail-row">
                    <span className="detail-label">
                      <FaMapMarkerAlt /> Ubicacion:
                    </span>
                    <span className="detail-value">
                      {formatLocation(selectedSignature)}
                    </span>
                  </div>
                )}

                {selectedSignature.captured_by_profile && (
                  <div className="detail-row">
                    <span className="detail-label">Capturada por:</span>
                    <span className="detail-value">
                      {selectedSignature.captured_by_profile.full_name}
                    </span>
                  </div>
                )}

                {selectedSignature.task && (
                  <div className="detail-row">
                    <span className="detail-label">Tarea:</span>
                    <span className="detail-value">
                      {selectedSignature.task.task_number} - {selectedSignature.task.title}
                    </span>
                  </div>
                )}

                {selectedSignature.notes && (
                  <div className="detail-notes">
                    <span className="detail-label">Notas:</span>
                    <p>{selectedSignature.notes}</p>
                  </div>
                )}
              </div>
            </div>

            <div className="modal-footer">
              <button
                className="close-btn"
                onClick={() => setSelectedSignature(null)}
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SignatureList;
